"use client";

import { useEffect, useRef } from "react";
import { DayButton, DayPicker, getDefaultClassNames } from "react-day-picker";

import { cn } from "@oneleet/utils";

import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";

const navButtonClassNames = cn(
  "text-neutral-550 aspect-square rounded-lg p-1",
  "transition-all duration-300 hover:duration-0",
  "hover:bg-accent-100 hover:text-accent-800",
);

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "group/calendar bg-white p-2 [--cell-size:--spacing(8)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn(
          "w-fit rounded-xl bg-white shadow-[0px_0px_0px_1px_rgba(35,34,34,0.08),0px_1px_1px_0px_rgba(35,34,34,0.05),0px_2px_4px_0px_rgba(35,34,34,0.08)]",
          defaultClassNames.root,
        ),
        months: cn(
          "relative flex flex-col gap-4 md:flex-row",
          defaultClassNames.months,
        ),
        month: cn("flex w-full flex-col gap-1.5", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1 py-0.5",
          defaultClassNames.nav,
        ),
        button_previous: cn(
          navButtonClassNames,
          "select-none aria-disabled:opacity-50",
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          navButtonClassNames,
          "select-none aria-disabled:opacity-50",
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          "px-(--cell-size) flex h-6 w-full items-center justify-center pt-[5px]",
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          "text-ng-md font-ng-medium flex h-4 w-full items-center justify-center gap-2 pt-0.5",
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          "relative rounded-md px-2 py-1 shadow-[0px_0px_0px_1px_rgba(35,34,34,0.08),0px_1px_1px_0px_rgba(35,34,34,0.05),0px_2px_4px_0px_rgba(35,34,34,0.08)]",
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn(
          "absolute inset-0 p-0 opacity-0",
          defaultClassNames.dropdown,
        ),
        caption_label: cn(
          "font-ng-medium select-none",
          captionLayout === "label"
            ? "text-ng-md"
            : "[&>svg]:text-neutral-550 text-ng-md flex items-center gap-1 rounded-md [&>svg]:size-3.5",
          defaultClassNames.caption_label,
        ),
        table: "w-full border-collapse",
        weekdays: cn(
          "flex border-b border-neutral-200",
          defaultClassNames.weekdays,
        ),
        weekday: cn(
          "text-neutral-550 flex-1 select-none rounded-md px-[3.5px] py-2 text-[0.8rem] font-normal",
          defaultClassNames.weekday,
        ),
        week: cn("mt-1 flex w-full", defaultClassNames.week),
        week_number_header: cn(
          "w-(--cell-size) select-none",
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          "text-neutral-550 select-none text-[0.8rem]",
          defaultClassNames.week_number,
        ),
        day: cn(
          "group/day text-ng-md relative flex aspect-square size-8 select-none items-center justify-center p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-lg [&:last-child[data-selected=true]_button]:rounded-r-lg",
          defaultClassNames.day,
        ),
        range_start: cn(defaultClassNames.range_start),
        range_middle: cn("rounded-none", defaultClassNames.range_middle),
        range_end: cn(defaultClassNames.range_end),
        today: cn(
          "[&_div]:border-accent-400 [&_div]:text-accent-900 [&_div]:font-ng-medium [&_div]:border [&_div]:shadow-[0px_1px_4px_0px_rgba(0,0,0,0.08),0px_1px_3px_0px_rgba(0,0,0,0.10)]",
          defaultClassNames.today,
        ),
        outside: cn(
          "text-neutral-550 aria-selected:text-neutral-550",
          defaultClassNames.outside,
        ),
        disabled: cn("text-neutral-550 opacity-50", defaultClassNames.disabled),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon className={cn("size-4", className)} {...props} />
            );
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon
                className={cn("size-4", className)}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon className={cn("size-4", className)} {...props} />
          );
        },
        DayButton: CalendarDayButton,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="size-(--cell-size) flex items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  children,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();

  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      data-day={day.date.toLocaleDateString()}
      data-today={modifiers.today}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        // Layout and sizing
        "group aspect-square h-full w-full",
        // Styling
        "rounded-[10px] p-0.5",
        // Transitions and interactions
        "transition-shadow duration-300 hover:duration-0",
        "hover:ring-accent-500 hover:shadow-[inset_0px_0px_0px_2px_#C5D1F7]",
        // Range start styles
        "data-[range-start=true]:before:bg-accent-200 data-[range-start=true]:before:absolute data-[range-start=true]:before:inset-y-0.5 data-[range-start=true]:before:right-0 data-[range-start=true]:before:z-10 data-[range-start=true]:before:w-[calc(100%-4px)] data-[range-start=true]:before:rounded-l-lg data-[range-start=true]:before:content-[''] data-[range-start=true]:hover:before:z-[1]",
        // Range middle styles
        "data-[range-middle=true]:before:bg-accent-200 data-[range-middle=true]:rounded-none data-[range-middle=true]:before:absolute data-[range-middle=true]:before:inset-x-0 data-[range-middle=true]:before:inset-y-0.5 data-[range-middle=true]:before:z-10 data-[range-middle=true]:before:content-[''] data-[range-middle=true]:hover:shadow-none data-[range-middle=true]:hover:ring-0",
        // Range end styles
        "data-[range-end=true]:before:bg-accent-200 data-[range-end=true]:before:absolute data-[range-end=true]:before:inset-y-0.5 data-[range-end=true]:before:left-0 data-[range-end=true]:before:z-10 data-[range-end=true]:before:w-[calc(100%-4px)] data-[range-end=true]:before:rounded-r-lg data-[range-end=true]:before:content-[''] data-[range-end=true]:hover:before:z-[1]",
        // Focus states
        "focus:outline-none",
        defaultClassNames.day,
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "text-ng-md relative z-20 flex aspect-square !size-full items-center justify-center rounded-lg p-1",
          // Selected single date styles
          "group-data-[selected-single=true]:bg-accent-800 group-data-[selected-single=true]:group-hover:bg-accent-900 group-data-[selected-single=true]:font-ng-medium group-data-[selected-single=true]:text-white",
          // Range start styles
          "group-data-[range-start=true]:bg-accent-800 group-data-[range-start=true]:group-hover:bg-accent-900 group-data-[range-start=true]:text-white",
          // Range middle styles
          "group-data-[range-middle=true]:group-hover:border-accent-400 group-data-[range-middle=true]:!h-[calc(100%-2px)] group-data-[range-middle=true]:rounded-lg group-data-[range-middle=true]:group-hover:border group-data-[range-middle=true]:group-hover:bg-white group-data-[range-middle=true]:group-hover:shadow-[0px_1px_4px_0px_rgba(0,0,0,0.08),0px_1px_3px_0px_rgba(0,0,0,0.10)]",
          // Range end styles
          "group-data-[range-end=true]:bg-accent-800 group-data-[range-end=true]:group-hover:bg-accent-900 group-data-[range-end=true]:text-white",
          // Layout and sizing
          "min-w-(--cell-size) flex aspect-square size-auto w-full flex-col gap-1 font-normal leading-none",
          "transition-all duration-300 group-hover:duration-0",
        )}
      >
        {children}
      </div>
    </button>
  );
}

export { Calendar, CalendarDayButton };
